import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, getApiError } from '../../lib/api';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { StatusPill } from '../../components/StatusPill';
import { Modal } from '../../components/Modal';
import { PageScaffold } from '../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../components/DataTable';

interface TrustedDevice {
  id: string;
  label: string;
  location: string;
  fingerprint: string;
  status: 'ACTIVE' | 'REVOKED';
  notes: string | null;
  issuedAt: string;
  lastSeenAt: string | null;
}

const FINGERPRINT_RE = /^[a-f0-9]{64}$/;
const fmtDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString() : 'never';
const fmtDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString() : '—');

export function DevicesPage(): JSX.Element {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<TrustedDevice | null>(null);
  const [editing, setEditing] = useState<TrustedDevice | null>(null);

  const devices = useQuery({
    queryKey: ['devices-admin'],
    queryFn: () => apiGet<TrustedDevice[]>('/admin/devices'),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'REVOKED' }) =>
      apiPatch(`/admin/devices/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['devices-admin'] }),
  });

  const list = devices.data ?? [];
  const activeCount = list.filter((d) => d.status === 'ACTIVE').length;
  const busyId = setStatus.isPending ? setStatus.variables?.id : undefined;

  return (
    <PageScaffold
      scroll={false}
      title="Devices"
      subtitle={`${activeCount} active · ${list.length} total — company devices allowed to use the ERP.`}
      actions={<Button onClick={() => setCreateOpen(true)}>+ Register device</Button>}
    >
      <DataTableCard>
        {devices.isLoading ? (
          <div className="p-10 text-center text-sm text-ink-3">Loading devices…</div>
        ) : list.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink-3">
            No devices registered yet. Issue one with{' '}
            <span className="kc-mono">ops/pki/issue-device.sh</span> and register its fingerprint.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
                <th className="px-6 py-2 font-medium">Device</th>
                <th className="px-6 py-2 font-medium">Location</th>
                <th className="px-6 py-2 font-medium">Last seen</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th className="px-6 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => setDetail(d)}
                  className="border-b border-rule-2 hover:bg-paper-2 cursor-pointer transition"
                >
                  <td className="px-6 py-3">
                    <span className="text-ink font-medium">{d.label}</span>
                    {d.notes && (
                      <span className="ml-2 align-middle text-[10px] uppercase tracking-wide text-ink-3 border border-rule rounded px-1 py-0.5">
                        note
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-ink-2">{d.location}</td>
                  <td className="px-6 py-3 text-xs text-ink-3 whitespace-nowrap">
                    {fmtDateTime(d.lastSeenAt)}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill tone={d.status === 'ACTIVE' ? 'positive' : 'negative'}>
                      {d.status === 'ACTIVE' ? 'active' : 'revoked'}
                    </StatusPill>
                  </td>
                  <td
                    className="px-6 py-3 text-right whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setDetail(d)}>
                        Details
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(d)}>
                        Edit
                      </Button>
                      {d.status === 'ACTIVE' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={busyId === d.id}
                          onClick={() => {
                            if (
                              window.confirm(`Revoke "${d.label}"? It loses access immediately.`)
                            ) {
                              setStatus.mutate({ id: d.id, status: 'REVOKED' });
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="primary"
                          loading={busyId === d.id}
                          onClick={() => setStatus.mutate({ id: d.id, status: 'ACTIVE' })}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>

      {detail && <DeviceDetailModal device={detail} onClose={() => setDetail(null)} />}
      {editing && <EditDeviceModal device={editing} onClose={() => setEditing(null)} />}
      {createOpen && <RegisterDeviceModal onClose={() => setCreateOpen(false)} />}
    </PageScaffold>
  );
}

function KV({ k, v }: { k: string; v: ReactNode }): JSX.Element {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-3">{k}</div>
      <div className="text-ink-2 mt-0.5">{v}</div>
    </div>
  );
}

function DeviceDetailModal({
  device,
  onClose,
}: {
  device: TrustedDevice;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <Modal
      title={device.label}
      subtitle={device.location}
      onClose={onClose}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <KV
            k="Status"
            v={
              <StatusPill tone={device.status === 'ACTIVE' ? 'positive' : 'negative'}>
                {device.status === 'ACTIVE' ? 'active' : 'revoked'}
              </StatusPill>
            }
          />
          <KV k="Location" v={device.location} />
          <KV k="Registered" v={fmtDate(device.issuedAt)} />
          <KV k="Last seen" v={fmtDateTime(device.lastSeenAt)} />
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-3 mb-1">
            Fingerprint (SHA-256)
          </div>
          <div className="flex items-start gap-2">
            <code className="kc-mono text-[11px] leading-relaxed text-ink break-all bg-paper-2 border border-rule rounded p-2 flex-1">
              {device.fingerprint}
            </code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void navigator.clipboard?.writeText(device.fingerprint).then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1200);
                });
              }}
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </Button>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-3 mb-1">Note</div>
          <div className="text-sm text-ink-2 whitespace-pre-wrap">
            {device.notes ?? <span className="text-ink-3">—</span>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Edit a registered device's label / location / notes. The fingerprint is the
// cert identity and is shown read-only — to change it, register a new device.
function EditDeviceModal({
  device,
  onClose,
}: {
  device: TrustedDevice;
  onClose: () => void;
}): JSX.Element {
  const [label, setLabel] = useState(device.label);
  const [location, setLocation] = useState(device.location);
  const [notes, setNotes] = useState(device.notes ?? '');
  const qc = useQueryClient();

  const submit = useMutation({
    mutationFn: () =>
      apiPatch(`/admin/devices/${device.id}`, {
        label,
        location,
        notes: notes || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices-admin'] });
      onClose();
    },
  });

  return (
    <Modal
      title={`Edit · ${device.label}`}
      subtitle="The fingerprint can't be changed — register a new device for a new certificate."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submit.isPending}
            disabled={!label || !location}
            onClick={() => submit.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Label (e.g. Bagatelle till 1)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
        />
        <Input
          label="Location (shop / mall)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          required
        />
        <Input
          label="Note (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Chavi's laptop — dev team"
        />
        <div>
          <div className="text-[10px] uppercase tracking-wide text-ink-3 mb-1">
            Fingerprint (SHA-256)
          </div>
          <code className="kc-mono text-[11px] leading-relaxed text-ink-2 break-all bg-paper-2 border border-rule rounded p-2 block">
            {device.fingerprint}
          </code>
        </div>
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}

function RegisterDeviceModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [label, setLabel] = useState('');
  const [location, setLocation] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const [notes, setNotes] = useState('');
  const qc = useQueryClient();

  const fingerprintValid = FINGERPRINT_RE.test(fingerprint);
  const submit = useMutation({
    mutationFn: () =>
      apiPost('/admin/devices', {
        label,
        location,
        fingerprint,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices-admin'] });
      onClose();
    },
  });

  return (
    <Modal
      title="Register device"
      subtitle="Paste the fingerprint printed by issue-device.sh."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={submit.isPending}
            disabled={!label || !location || !fingerprintValid}
            onClick={() => submit.mutate()}
          >
            Register
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Label (e.g. Bagatelle till 1)"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
        />
        <Input
          label="Location (shop / mall)"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          required
        />
        <Input
          label="Fingerprint (SHA-256, lowercase hex, no colons)"
          value={fingerprint}
          onChange={(e) => setFingerprint(e.target.value.trim().toLowerCase())}
          maxLength={64}
          required
          hint={fingerprint && !fingerprintValid ? 'Must be 64 hex characters.' : undefined}
        />
        <Input
          label="Note (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Chavi's laptop — dev team"
        />
        {submit.isError && (
          <div className="text-xs text-negative">{getApiError(submit.error).message}</div>
        )}
      </div>
    </Modal>
  );
}
